import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Archive,
  ArchiveOpen,
  Building,
  Delete,
  Download,
  Pencil,
  People,
  Pin,
  Settings,
  Upload,
} from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  ItAssetsStatusCountsDocument,
  ItAssetsTableDocument,
  ItAssetsTableQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useModuleAvailability } from 'modules/core/hooks/useModuleAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import {
  getDeletedStatusTranslation,
  getDeletedStatusVariant,
  safeAssetStatusAsVariant,
  safeAssetTranslateStatus,
  safeAssetTranslateType,
  safeAssetTypeAsIcon,
} from 'modules/it_management/components/AssetsList/utils'
import {
  isValidAssetStatus,
  isValidAssetType,
} from 'modules/it_management/components/AssetsList/utils'
import { BulkChangeAssetOwnershipModal } from 'modules/it_management/components/BulkChangeAssetOwnership'
import { BulkDeleteItAssetsModal } from 'modules/it_management/components/BulkDeleteItAssetsModal/BulkDeleteItAssetsModal'
import { ChangeAssetOwnershipModal } from 'modules/it_management/components/ChangeAssetOwnership/ChangeAssetOwnershipModal'
import { DeleteItAssetModal } from 'modules/it_management/components/DeleteItAsset/DeleteItAssetModal'
import { ItAssetCompleteMaintenanceModal } from 'modules/it_management/components/ItAssetCompleteMaintenanceModal/ItAssetCompleteMaintenanceModal'
import { ItAssetMaintenanceModal } from 'modules/it_management/components/ItAssetMaintenanceModal/ItAssetMaintenanceModal'
import { NewEquipmentModal } from 'modules/it_management/components/NewEquipmentModal'
import { RecoverItAssetModal } from 'modules/it_management/components/RecoverItAsset/RecoverItAssetModal'
import { RetireItAssetModal } from 'modules/it_management/components/RetireItAsset/RetireItAssetModal'
import { useEmployees } from 'modules/it_management/hooks/useEmployees'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import { useLocations } from 'modules/it_management/hooks/useLocations'
import { useTeams } from 'modules/it_management/hooks/useTeams'
import { useWorkplaces } from 'modules/it_management/hooks/useWorkplaces'
import { getAssetTypeSelectOptions } from 'modules/it_management/lib/constants'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

import { useGrouping } from './useGrouping'

type GroupingStrategy = 'status' | 'type'

type Client = ItAssetsTableQuery['itManagement']['itAssetsConnection']['nodes'][number]

interface SelectedItem {
  item: Client
  checked: boolean
}

const isAssetDeleted = (item: Client): boolean => {
  return 'discardedAt' in item && !!item.discardedAt
}

interface BulkActionParams {
  itemsStatus: readonly SelectedItem[]
}

const Container: React.FC = () => {
  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()
  const [showModal, setShowModal] = useState<boolean>(false)
  const { employees } = useEmployees()
  const { locations } = useLocations()
  const { workplaces } = useWorkplaces()
  const { teams } = useTeams()
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([])
  const [selectedAssets, setSelectedAssets] = useState<Client[]>([])
  const [groupingStrategy, _setGroupingStrategy] = useState<GroupingStrategy>('status')
  const [assetToDelete, setAssetToDelete] = useState<Client | null>(null)
  const [assetToRetire, setAssetToRetire] = useState<Client | null>(null)
  const [assetToRecover, setAssetToRecover] = useState<Client | null>(null)
  const [assetToAssignOwner, setAssetToAssignOwner] = useState<Client | null>(null)
  const [assetToMaintenance, setAssetToMaintenance] = useState<Client | null>(null)
  const [assetToCompleteMaintenance, setAssetToCompleteMaintenance] = useState<Client | null>(null)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState<boolean>(false)
  const [showBulkAssignModal, setShowBulkAssignModal] = useState<boolean>(false)
  const [showExportModal, setShowExportModal] = useState<boolean>(false)

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { company } = useCurrentSession()

  const { moduleAvailability: spaceControlAvailability } = useModuleAvailability(
    AvailableModules.space_control,
    company.id
  )

  const hasSpaceControl = spaceControlAvailability.available

  const { data: statusCountsData } = useQuery(ItAssetsStatusCountsDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const { mapData, grouping } = useGrouping({
    groupingStrategy,
    statuses: [],
    typeNames: [],
  })

  const [statusCounts, setStatusCounts] = useState({
    inStock: 0,
    assigned: 0,
    maintenance: 0,
    retired: 0,
    deleted: 0,
    total: 0,
  })

  const openModal = () => {
    track(trackingEvents.itManagement.newDevice.createEquipment)
    setShowModal(true)
  }

  const handleImport = () => {
    track(trackingEvents.itManagement.importAssets.importStar)
    navigate(navigation.it_management.importAssets.fullPath().replace(/\*+$/, ''))
  }

  const handleExport = () => {
    setShowExportModal(true)
  }

  const handleDeleteAsset = (asset: Client) => {
    setAssetToDelete(asset)
  }

  const handleRetireAsset = (asset: Client) => {
    setAssetToRetire(asset)
  }

  const handleRecoverAsset = (asset: Client) => {
    setAssetToRecover(asset)
  }

  const handleBulkDeleteAssets = () => {
    setShowBulkDeleteModal(true)
  }

  const handleBulkAssignAssets = () => {
    setShowBulkAssignModal(true)
  }

  const handleSetMaintenance = (asset: Client) => {
    setAssetToMaintenance(asset)
  }

  const handleCompleteMaintenance = (asset: Client) => {
    setAssetToCompleteMaintenance(asset)
  }

  useEffect(() => {
    const handler = (e: FocusEvent) => {
      const t = e.target
      const pageContainer = document.querySelector('#it-management-data-collection')

      if (
        t instanceof HTMLInputElement &&
        (t.placeholder || '').toLowerCase().includes(i18n.t('common.search').toLowerCase()) &&
        pageContainer instanceof HTMLElement &&
        pageContainer.contains(t)
      ) {
        track(trackingEvents.itManagement.searchForInventory)
      }
    }
    document.addEventListener('focus', handler, true)
    return () => document.removeEventListener('focus', handler, true)
  }, [])

  useEffect(() => {
    if (!statusCountsData) return
    setStatusCounts({
      inStock: statusCountsData.itManagement.inStockCount.totalCount ?? 0,
      assigned: statusCountsData.itManagement.assignedCount.totalCount ?? 0,
      maintenance: statusCountsData.itManagement.maintenanceCount.totalCount ?? 0,
      retired: statusCountsData.itManagement.retiredCount.totalCount ?? 0,
      deleted: statusCountsData.itManagement.deletedCount.totalCount ?? 0,
      total: statusCountsData.itManagement.totalCount.totalCount ?? 0,
    })
  }, [statusCountsData])

  const dataSourceOptions = useMemo(() => {
    const presets = [
      {
        label: i18n.t('it_management.assets.presets.all_assets'),
        filter: {
          status: [],
          includeDeleted: false,
        },
        itemsCount: () => statusCounts.total,
      },
      {
        label: i18n.t('it_management.assets.status.in_stock'),
        filter: {
          status: ['in_stock'],
          includeDeleted: false,
        },
        itemsCount: () => statusCounts.inStock,
      },
      {
        label: i18n.t('it_management.assets.status.assigned'),
        filter: {
          status: ['assigned'],
          includeDeleted: false,
        },
        itemsCount: () => statusCounts.assigned,
      },
      {
        label: i18n.t('it_management.assets.status.maintenance'),
        filter: {
          status: ['maintenance'],
          includeDeleted: false,
        },
        itemsCount: () => statusCounts.maintenance,
      },
      {
        label: i18n.t('it_management.assets.status.retired'),
        filter: {
          status: ['retired'],
          includeDeleted: false,
        },
        itemsCount: () => statusCounts.retired,
      },
      {
        label: i18n.t('it_management.assets.status.deleted'),
        filter: {
          status: [],
          includeDeleted: true,
        },
        itemsCount: () => statusCounts.deleted,
      },
    ]

    return {
      document: ItAssetsTableDocument,
      mapData: (tableData: ItAssetsTableQuery) => mapData(tableData),
      grouping,
      perPage: 25,
      sortings: {
        createdAt: {
          label: i18n.t('it_management.common.created_at'),
          sortBy: 'createdAt',
        },
        modelName: {
          label: i18n.t('it_management.common.name'),
          sortBy: 'modelName',
        },
        typeName: {
          label: i18n.t('it_management.common.type'),
          sortBy: 'typeName',
        },
        status: {
          label: i18n.t('it_management.common.status'),
          sortBy: 'status',
        },
        serialNumber: {
          label: i18n.t('it_management.common.serial_number'),
          sortBy: 'serialNumber',
        },
      },
      defaultSortings: {
        field: 'createdAt' as const,
        order: 'desc' as const,
      },
      search: {
        enabled: true,
      },
      filters: {
        status: {
          type: 'in' as const,
          label: i18n.t('it_management.common.status'),
          options: {
            options: async () => [
              { label: i18n.t('it_management.assets.status.in_stock'), value: 'in_stock' },
              { label: i18n.t('it_management.assets.status.assigned'), value: 'assigned' },
              { label: i18n.t('it_management.assets.status.maintenance'), value: 'maintenance' },
              { label: i18n.t('it_management.assets.status.retired'), value: 'retired' },
            ],
          },
        },
        type: {
          type: 'in' as const,
          label: i18n.t('it_management.common.type'),
          options: {
            options: async () => getAssetTypeSelectOptions(),
          },
        },
        owner: {
          type: 'in' as const,
          label: i18n.t('it_management.common.person'),
          options: {
            cache: false,
            options: employees,
          },
        },
        ...(hasSpaceControl && {
          location: {
            type: 'in' as const,
            label: i18n.t('it_management.common.space'),
            options: {
              cache: false,
              options: locations,
            },
          },
        }),
        workplace: {
          type: 'in' as const,
          label: i18n.t('it_management.common.workplace'),
          options: {
            cache: false,
            options: workplaces,
          },
        },
        team: {
          type: 'in' as const,
          label: i18n.t('it_management.common.team'),
          options: {
            cache: false,
            options: teams,
          },
        },
      },
      presets,
      primaryActions: () => ({
        label: i18n.t('it_management.assets.actions.new_equipment'),
        icon: Add,
        onClick: () => openModal(),
      }),
      secondaryActions: () => [
        {
          label: i18n.t('it_management.assets.actions.import_assets'),
          icon: Upload,
          onClick: () => handleImport(),
        },
        {
          label: i18n.t('it_management.assets.actions.export_assets'),
          icon: Download,
          onClick: () => handleExport(),
        },
      ],
      selectable: (item: Client) => item.id,
      bulkActions: (selectedItems: BulkActionParams) => {
        const hasDeletedItems = selectedItems.itemsStatus.some(
          (itemStatus) => itemStatus.checked && isAssetDeleted(itemStatus.item)
        )
        if (hasDeletedItems) {
          return { primary: [], secondary: [] }
        }

        return {
          primary: [
            {
              id: 'delete-assets',
              label: i18n.t('it_management.assets.bulk_actions.delete_assets'),
              icon: Delete,
            },
          ],
          secondary: [
            {
              id: 'assign-owner',
              label: i18n.t('it_management.assets.bulk_assign.assign'),
              icon: People,
            },
          ],
        }
      },
      itemActions: (item: Client) => {
        if (isAssetDeleted(item)) {
          return []
        }

        return [
          {
            label: i18n.t('it_management.assets.actions.edit_asset'),
            icon: Pencil,
            onClick: () => {
              navigate(navigation.it_management.edit.fullPath({ assetId: item.id }))
            },
          },
          {
            label: i18n.t('it_management.assets.actions.assign'),
            icon: People,
            onClick: () => {
              track(trackingEvents.itManagement.assigmentEquipment.startTable)

              setAssetToAssignOwner(item)
            },
          },
          ...(item.status === 'maintenance'
            ? [
                {
                  label: i18n.t('it_management.assets.actions.complete_maintenance'),
                  icon: Settings,
                  onClick: () => {
                    handleCompleteMaintenance(item)
                  },
                },
              ]
            : [
                {
                  label: i18n.t('it_management.assets.actions.set_maintenance'),
                  icon: Settings,
                  onClick: () => {
                    handleSetMaintenance(item)
                  },
                },
              ]),
          ...(item.status === 'retired'
            ? [
                {
                  label: i18n.t('it_management.assets.actions.restore_asset'),
                  icon: ArchiveOpen,
                  onClick: () => {
                    handleRecoverAsset(item)
                  },
                },
              ]
            : [
                {
                  label: i18n.t('it_management.assets.actions.retire_asset'),
                  icon: Archive,
                  onClick: () => {
                    handleRetireAsset(item)
                  },
                },
              ]),
          {
            label: i18n.t('it_management.assets.actions.delete_asset'),
            icon: Delete,
            onClick: () => {
              handleDeleteAsset(item)
            },
            critical: true,
          },
        ]
      },
      mapVariables: (variables: {
        search?: string
        sortings?: Array<{ field: string; order: string }>
        filters?: {
          status?: string[]
          type?: string[]
          owner?: number[]
          location?: number[]
          workplace?: number[]
          team?: number[]
          includeDeleted?: boolean
        }
      }) => {
        const hasStatusFilters = variables.filters?.status && variables.filters.status.length > 0
        const includeDeleted = variables.filters?.includeDeleted === true

        const normalizedFilters = {
          ...variables.filters,
          includeDeleted: includeDeleted && !hasStatusFilters ? true : undefined,
          status: includeDeleted ? [] : variables.filters?.status,
        }

        return {
          search: variables.search,
          sort: variables.sortings?.[0],
          statuses:
            normalizedFilters.status && normalizedFilters.status.length > 0
              ? normalizedFilters.status.filter(isValidAssetStatus)
              : undefined,
          typeNames:
            normalizedFilters.type && normalizedFilters.type.length > 0
              ? normalizedFilters.type.filter(isValidAssetType)
              : undefined,
          ownerIds:
            normalizedFilters.owner && normalizedFilters.owner.length > 0
              ? normalizedFilters.owner
              : undefined,
          locationIds:
            normalizedFilters.location && normalizedFilters.location.length > 0
              ? normalizedFilters.location
              : undefined,
          workplaceIds:
            normalizedFilters.workplace && normalizedFilters.workplace.length > 0
              ? normalizedFilters.workplace
              : undefined,
          teamIds:
            normalizedFilters.team && normalizedFilters.team.length > 0
              ? normalizedFilters.team
              : undefined,
          includeDeleted: normalizedFilters.includeDeleted,
        }
      },
      mapPagination: (tableData: ItAssetsTableQuery) => ({
        total: tableData.itManagement.itAssetsConnection.totalCount ?? 0,
      }),
      itemUrl: (item: Client) => navigation.it_management.assetId.fullPath({ assetId: item.id }),
    }
  }, [
    statusCounts,
    navigation,
    openModal,
    handleImport,
    mapData,
    employees,
    locations,
    workplaces,
    teams,
    grouping,
    navigate,
    hasSpaceControl,
  ])

  const source = useGraphqlDataCollectionSource(dataSourceOptions, [
    employees,
    locations,
    workplaces,
    teams,
  ])

  return (
    <ResponsivePage.Body
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading}
      variant="full"
    >
      <Outlet />

      <Box id="it-management-data-collection">
        <OneDataCollection
          source={source}
          emptyStates={{
            'no-data': {
              emoji: '💻',
              title: i18n.t('it_management.empty_state.title'),
              description: i18n.t('it_management.empty_state.description'),
              actions: [
                {
                  label: i18n.t('it_management.empty_state.import_button'),
                  icon: Upload,
                  onClick: () => handleImport(),
                  variant: 'outline',
                },
              ],
            },
          }}
          onBulkAction={(action: string, selectedItems: BulkActionParams) => {
            const selectedCheckedItems = selectedItems.itemsStatus.filter((item) => item.checked)
            setSelectedAssetIds(selectedCheckedItems.map((itemStatus) => itemStatus.item.id))
            setSelectedAssets(selectedCheckedItems.map((itemStatus) => itemStatus.item))

            switch (action) {
              case 'assign-owner':
                handleBulkAssignAssets()
                break
              case 'delete-assets':
                handleBulkDeleteAssets()
                break
            }
          }}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('it_management.common.device_title'),
                    render: (item: Client) =>
                      item.itAssetModel?.brand + ' ' + item.itAssetModel?.name,
                    sorting: 'modelName',
                  },
                  {
                    label: i18n.t('it_management.common.status'),
                    render: (item: Client) => ({
                      type: 'status',
                      value: {
                        status: isAssetDeleted(item)
                          ? getDeletedStatusVariant()
                          : safeAssetStatusAsVariant(item.status),
                        label: isAssetDeleted(item)
                          ? getDeletedStatusTranslation()
                          : safeAssetTranslateStatus(item.status),
                      },
                    }),
                    sorting: 'status',
                  },
                  {
                    label: i18n.t('it_management.common.assigned_to'),
                    render: (item: Client) => {
                      if (item.owner) {
                        return {
                          type: 'person',
                          value: {
                            firstName: item.owner.firstName ?? '',
                            lastName: item.owner.lastName ?? '',
                            src: buildUrl(item.owner?.avatar?.url ?? ''),
                          },
                        }
                      }

                      if (item.workplace?.name) {
                        return {
                          type: 'icon',
                          value: {
                            icon: Pin,
                            label: item.workplace.name,
                          },
                        }
                      }

                      if (item.team?.name) {
                        return {
                          type: 'team',
                          value: {
                            name: item.team.name,
                            src: '',
                          },
                        }
                      }

                      if (item.location?.name) {
                        return {
                          type: 'icon',
                          value: {
                            icon: Building,
                            label: item.location.name,
                          },
                        }
                      }

                      return {
                        type: 'text',
                        value: '-',
                      }
                    },
                  },
                  {
                    label: i18n.t('it_management.common.type'),
                    render: (item: Client) => ({
                      type: 'icon',
                      value: {
                        icon: safeAssetTypeAsIcon(item.itAssetModel?.typeName ?? ''),
                        label: safeAssetTranslateType(item.itAssetModel?.typeName ?? ''),
                      },
                    }),
                    sorting: 'typeName',
                  },
                  {
                    label: i18n.t('it_management.common.serial_number'),
                    render: (item: Client) => ({
                      type: 'tag',
                      value: {
                        label: item.serialNumber || '-',
                      },
                    }),
                    sorting: 'serialNumber',
                  },
                  {
                    label: i18n.t('it_management.common.internal_label'),
                    render: (item: Client) => ({
                      type: 'tag',
                      value: {
                        label: item.label || '-',
                      },
                    }),
                  },
                ],
              },
            },
          ]}
        />
      </Box>

      {showModal && <NewEquipmentModal onClose={() => setShowModal(false)} />}
      {assetToRecover && (
        <RecoverItAssetModal asset={assetToRecover} onClose={() => setAssetToRecover(null)} />
      )}

      {assetToRetire && (
        <RetireItAssetModal asset={assetToRetire} onClose={() => setAssetToRetire(null)} />
      )}

      {assetToDelete && (
        <DeleteItAssetModal asset={assetToDelete} onClose={() => setAssetToDelete(null)} />
      )}
      {assetToAssignOwner && (
        <ChangeAssetOwnershipModal
          asset={assetToAssignOwner}
          onClose={() => setAssetToAssignOwner(null)}
        />
      )}
      {showBulkDeleteModal && (
        <BulkDeleteItAssetsModal
          ids={selectedAssetIds}
          onClose={() => setShowBulkDeleteModal(false)}
        />
      )}
      {showBulkAssignModal && (
        <BulkChangeAssetOwnershipModal
          ids={selectedAssetIds}
          assets={selectedAssets}
          onClose={() => setShowBulkAssignModal(false)}
        />
      )}
      {assetToMaintenance && (
        <ItAssetMaintenanceModal
          asset={assetToMaintenance}
          onClose={() => setAssetToMaintenance(null)}
        />
      )}
      {assetToCompleteMaintenance && (
        <ItAssetCompleteMaintenanceModal
          asset={assetToCompleteMaintenance}
          onClose={() => setAssetToCompleteMaintenance(null)}
        />
      )}
      {showExportModal && (
        <ExportSidepanel
          resource="it_management.it_asset"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </ResponsivePage.Body>
  )
}

const ClientsListPage: React.FC = () => {
  return <Container />
}

export default ClientsListPage
